import { FileFolderPath, Folder } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { useRoutingContext } from 'app/routing_context'
import { useConversation } from 'chat/conversations/queries/detail'
import { useFileFolderActions } from 'file_folder_repository/context'
import { useSharedChatGroupFileFolders } from 'file_folder_repository/queries/list'
import {
  filterResourceList,
  getFileFolderList,
  sortResourceList,
} from 'file_folder_repository/util'
import { FILTER_ITEMS } from 'file_folder_repository/views/filters_row'
import { SharedFileFoldersScreenProps } from 'home/stack/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { showHeaderButton } from 'shared/header/header_button'
import { SharedFileFoldersView } from './views'

export function SharedFileFoldersScreen({
  navigation,
  route,
}: SharedFileFoldersScreenProps) {
  const { conversationId, path } = route.params

  const {
    onLongPressResource,
    setConversationId,
    openSortOrderBottomSheet,
    sortOption,
    onPressFile,
    setShouldNavigateToFoldersScreen,
  } = useFileFolderActions()
  const { theme } = useTheme()
  const { handleNavigation } = useRoutingContext()
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0)
  const [currentFolderId, setCurrentFolderId] = useState<string>()
  const conversationQuery = useConversation(conversationId)

  const fileFolderRepositoryQuery = useSharedChatGroupFileFolders(
    conversationId,
    currentFolderId
  )

  const filterItems = useMemo(
    () => [
      FILTER_ITEMS.all,
      FILTER_ITEMS.documents,
      FILTER_ITEMS.folders,
      FILTER_ITEMS.photosVideos,
      FILTER_ITEMS.webLinks,
    ],
    []
  )

  const sortedFileFolderList = useMemo(
    () =>
      filterResourceList(
        sortResourceList(
          getFileFolderList(fileFolderRepositoryQuery.data),
          sortOption
        ),
        selectedFilterIndex
      ),
    [fileFolderRepositoryQuery.data, selectedFilterIndex, sortOption]
  )

  const HomeBreadCrumb: FileFolderPath = { id: undefined, name: 'Home' }

  const [breadcrumbs, setBreadcrumbs] = useState<FileFolderPath[]>(
    path ? [HomeBreadCrumb, ...path] : [HomeBreadCrumb]
  )

  const onPressFolder = useCallback(
    (folder: Folder) => {
      setCurrentFolderId(folder.id)
      setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }])
      setSelectedFilterIndex(0)
    },
    [setCurrentFolderId]
  )

  const onPressBreadcrumb = useCallback(
    (index: number) => {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1)
      setCurrentFolderId(newBreadcrumbs[newBreadcrumbs.length - 1]?.id)
      setBreadcrumbs(newBreadcrumbs)
      setSelectedFilterIndex(0)
    },
    [breadcrumbs, setCurrentFolderId]
  )

  const onPressSearchBarButton = useCallback(() => {
    handleNavigation({
      routeName: 'file_folder_search',
      routeParams: { conversationId },
    })
  }, [conversationId, handleNavigation])

  useEffect(() => {
    setConversationId(conversationId)
  }, [conversationId, setConversationId])

  useEffect(() => {
    setShouldNavigateToFoldersScreen(false)
    return () => {
      setCurrentFolderId(undefined)
      setShouldNavigateToFoldersScreen(true)
    }
  }, [setCurrentFolderId, setShouldNavigateToFoldersScreen])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      onPress: () => {
        openSortOrderBottomSheet()
      },
      useIcon: true,
      title: 'sort',
      iconType: 'material-icons',
      theme,
    })
  }, [navigation, openSortOrderBottomSheet, theme])

  return (
    <SharedFileFoldersView
      conversation={conversationQuery.data}
      chatGroupFileFolderList={sortedFileFolderList}
      breadcrumbs={breadcrumbs}
      onLongPressResource={onLongPressResource}
      onPressBreadcrumb={onPressBreadcrumb}
      onPressFile={onPressFile}
      onPressFolder={onPressFolder}
      loading={fileFolderRepositoryQuery.isLoading}
      selectedFilterIndex={selectedFilterIndex}
      setSelectedFilterIndex={setSelectedFilterIndex}
      filterItems={filterItems}
      onPressSearchBarButton={onPressSearchBarButton}
      sortOption={sortOption}
    />
  )
}
